import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  type ElementRef,
  HostListener,
  inject,
  Injector,
  type OnInit,
  PLATFORM_ID,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { isPlatformBrowser } from '@angular/common';
import { map } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { Router, RouterModule } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { Nav } from '../../enums/nav.enum';
import { NotificationType } from '../../enums/notificationtype.enum';
import { StorageEnum } from '../../enums/storage.enum';
import { BiometricsService } from '../../services/biometrics.service';
import { CmsService } from '../../services/cms.service';
import { NotificationsService } from '../../services/notifications.service';
import { StorageService } from '../../services/storage.service';
import { UtilService } from '../../services/util.service';
import { NotificationComponent } from '../notification/notification.component';
import type { BioTermsLabels } from '../../models/bio-terms-labels';

const FADE_CONTAINER_HEIGHT_PX = 250;

@Component({
  selector: 'app-bio-terms',
  imports: [MatButtonModule, RouterModule, NotificationComponent],
  templateUrl: './bio-terms.component.html',
  styleUrl: './bio-terms.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BioTermsComponent implements OnInit {
  // Dependency injection using inject()
  private readonly platformId = inject(PLATFORM_ID);
  private readonly bioS = inject(BiometricsService);
  private readonly cmsS = inject(CmsService);
  private readonly notifiS = inject(NotificationsService);
  private readonly router = inject(Router);
  private readonly storageS = inject(StorageService);
  private readonly utilS = inject(UtilService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  readonly isBrowser = isPlatformBrowser(this.platformId);
  readonly env = environment;
  readonly pageName = Nav.BioTerms;

  /** ViewChild reference to the scrollable fade container */
  readonly fadeContainer = viewChild<ElementRef<HTMLDivElement>>('fadeContainer');

  /** CMS labels as a signal */
  private readonly labelsSource = toSignal(
    this.cmsS
      .getContent('biometrics-terms', 'Biometrics Terms')
      .pipe(map((data) => this.cmsS.flattenBuilderReferencesPreservingKeys(data) as BioTermsLabels)),
  );

  constructor() {
    // Use effect() to hide spinner reactively - works for both fresh fetches and cached data
    effect(() => {
      if (this.labels()) {
        this.utilS.hideSpinner();
      }
    });
  }

  /** Computed labels for template binding */
  readonly labels = computed(() => this.labelsSource());

  redirectRoute = Nav.AccountSummary;

  @HostListener('scroll', ['$event'])
  onscroll(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.utilS.elementIsVisible(document.querySelector('.remove-fade'))) {
      target.classList.remove('gs-fade');
    } else {
      target.classList.add('gs-fade');
    }
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      this.redirectRoute = this.storageS.getSession(StorageEnum.BioRedirectRoute) || Nav.AccountSummary;

      // Set element height after render instead of polling with setInterval
      afterNextRender(
        () => {
          const el = this.fadeContainer()?.nativeElement;
          if (el) {
            this.utilS.setElementHeight(el, FADE_CONTAINER_HEIGHT_PX);
          }
        },
        { injector: this.injector },
      );
    }
  }

  /** Handles user acceptance of biometrics terms */
  accept(): void {
    const currentLabels = this.labels();
    if (!currentLabels) return;

    this.bioS
      .enrollUserInBiometrics(this.pageName, currentLabels.bioPopup)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.bioS.bioInfo.UsernameMatch = true;

        // Store username in localStorage (biometrics only runs on native platforms where cookies don't persist)
        const username = this.storageS.getSession(StorageEnum.UserName);
        if (username) {
          this.storageS.setLocal(StorageEnum.RememberedUsername, username);
        }
        const bioTypeText = this.bioS.getAvailableBiometricsText();
        const replacedBioText = currentLabels.biometricSuccessText.replace(/{BIOTYPE}/g, bioTypeText);

        this.notifiS.addNotification(replacedBioText, NotificationType.Postnotification, this.redirectRoute);
        this.storageS.removeSession(StorageEnum.BioRedirectRoute);
        void this.router.navigateByUrl(this.redirectRoute);
      });
  }

  /** Handles user cancellation of biometrics terms */
  cancel(): void {
    const currentLabels = this.labels();
    if (!currentLabels) return;

    this.bioS.bioInfo.UsernameMatch = false;
    const bioTypeText = this.bioS.getAvailableBiometricsText();
    const replacedBioText = currentLabels.biometricFailureText.replace(/{BIOTYPE}/g, bioTypeText);
    this.notifiS.addNotification(replacedBioText, NotificationType.Alert, this.redirectRoute);
    this.storageS.removeSession(StorageEnum.BioRedirectRoute);
    void this.router.navigateByUrl(this.redirectRoute);
  }
}
